import { type ComponentRef, Directive, type Type, effect, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextMenuDirective } from '@frontendlib/components/features/popover/directives/context-menu.directive';
import type { PopoverPlacement } from '@frontendlib/components/features/popover/types/placement';
import type { PopoverTrigger } from '@frontendlib/components/features/popover/types/trigger';
import { FormsLibraryOptionsComponent } from '../components/forms-library-options/forms-library-options.component';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';

/**
 * Opens a form list's sorting & filters menu from the list header's filter icon (`app-context-menu-state-indicator`),
 * and keeps the icon's reset cross in step with the list's options — as the Notes list does.
 */
@Directive({
  selector: '[appFormsLibraryOptions]',
  standalone: true,
})
export class FormsLibraryOptionsDirective extends ContextMenuDirective {
  @Input() public override stchPopoverTrigger: PopoverTrigger = 'click';
  @Input() public override stchPopoverPlacement: PopoverPlacement = 'bottomLeft';

  @Input('appFormsLibraryOptions') public scope: '' | 'folders' = '';

  private readonly listStore = inject(FormsWorkspaceStore);

  private readonly syncIndicator = effect(() => {
    this.isDefaultStateObservable?.next(this.scope === 'folders' ? this.listStore.isDefaultFolderOptions() : this.listStore.isDefaultOptions());
  });

  /** The indicator's reset cross pushes `true`; its initial `null` and the store's own echo above are no-ops. */
  private readonly resetFromIndicator = this.isDefaultStateObservable?.pipe(takeUntilDestroyed()).subscribe((isDefault) => {
    if (isDefault && this.scope === 'folders' && !this.listStore.isDefaultFolderOptions()) {
      this.listStore.resetFolderOptions();
    } else if (isDefault && this.scope !== 'folders' && !this.listStore.isDefaultOptions()) {
      this.listStore.resetOptions();
    }
  });

  protected registerComponent(): Type<FormsLibraryOptionsComponent> {
    return FormsLibraryOptionsComponent;
  }

  protected registerInstance(componentRef: ComponentRef<FormsLibraryOptionsComponent>): void {
    componentRef.instance.listStore = this.scope === 'folders' ? (this.listStore.foldersMenu as unknown as FormsWorkspaceStore) : this.listStore;
    componentRef.instance.folders = this.scope === 'folders';
  }
}
